import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminUserService } from '../../shared/services';
import type { UserRole, UpdateUserDTO } from '../../shared/types';
import { Save, X, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export function AdminUserFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    phoneNumber: '',
    senha: '',
    roles: [] as UserRole[],
    ativo: true,
  });

  const { data: user } = useQuery({
    queryKey: ['admin-user', id],
    queryFn: () => adminUserService.getUser(id!),
    enabled: isEditing,
  });

  useEffect(() => {
    if (user) {
      setFormData({
        nome: user.nome || user.name || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        senha: '',
        roles: user.roles || [],
        ativo: user.ativo ?? user.isActive ?? true,
      });
    }
  }, [user]);

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => adminUserService.createUser(data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      navigate('/admin/usuarios');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateUserDTO) => adminUserService.updateUser(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-user', id] });
      navigate('/admin/usuarios');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isEditing) {
      const updateData: UpdateUserDTO = {
        nome: formData.nome,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        senha: formData.senha || undefined,
        roles: formData.roles,
        ativo: formData.ativo,
      };
      updateMutation.mutate(updateData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const toggleRole = (role: UserRole) => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role],
    }));
  };

  return (
    <div className="p-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/admin/usuarios"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para usuários
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditing ? 'Editar Usuário' : 'Novo Usuário'}
          </h1>
          <p className="text-gray-600 mt-2">
            {isEditing 
              ? 'Atualize as informações do usuário'
              : 'Preencha os dados para criar um novo usuário'}
          </p>
        </div>

        {/* Formulário */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="space-y-6">
            {/* Nome */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome Completo *
              </label>
              <input
                type="text"
                required
                value={formData.nome}
                onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: João Silva"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="joao.silva@exemplo.com"
              />
            </div>

            {/* Senha */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Senha {!isEditing && '*'}
              </label>
              <input
                type="password"
                required={!isEditing}
                value={formData.senha}
                onChange={(e) => setFormData(prev => ({ ...prev, senha: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={isEditing ? 'Deixe em branco para não alterar' : 'Mínimo 6 caracteres'}
                minLength={6}
              />
              {isEditing && (
                <p className="text-sm text-gray-500 mt-1">
                  Deixe em branco para manter a senha atual
                </p>
              )}
            </div>

            {/* Perfis */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Perfis de Acesso *
              </label>
              <div className="space-y-3">
                <label className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.roles.includes('admin')}
                    onChange={() => toggleRole('admin')}
                    className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Administrador</p>
                    <p className="text-sm text-gray-600">
                      Acesso total ao sistema, incluindo gerenciamento de usuários, cursos e trilhas
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.roles.includes('instrutor')}
                    onChange={() => toggleRole('instrutor')}
                    className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Instrutor</p>
                    <p className="text-sm text-gray-600">
                      Pode criar e gerenciar cursos, acessar relatórios de alunos
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.roles.includes('aluno')}
                    onChange={() => toggleRole('aluno')}
                    className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Aluno</p>
                    <p className="text-sm text-gray-600">
                      Acesso aos cursos, trilhas e conteúdos de aprendizado
                    </p>
                  </div>
                </label>
              </div>
              {formData.roles.length === 0 && (
                <p className="text-sm text-red-600 mt-2">
                  Selecione pelo menos um perfil
                </p>
              )}
            </div>

            {/* Status */}
            <div>
              <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.ativo}
                  onChange={(e) => setFormData(prev => ({ ...prev, ativo: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <div>
                  <p className="font-medium text-gray-900">Usuário Ativo</p>
                  <p className="text-sm text-gray-600">
                    Desmarque para desativar o acesso do usuário
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Ações */}
          <div className="flex items-center gap-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="submit"
              disabled={formData.roles.length === 0 || createMutation.isPending || updateMutation.isPending}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="w-5 h-5" />
              {isEditing ? 'Salvar Alterações' : 'Criar Usuário'}
            </button>
            <Link
              to="/admin/usuarios"
              className="flex items-center gap-2 px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <X className="w-5 h-5" />
              Cancelar
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
